/**
 * Hero banner Rive — single canvas, breakpoint swap, loop, cached loads.
 */
(function () {
  'use strict';

  var RIVE_CDN = 'https://cdn.jsdelivr.net/npm/@rive-app/canvas-lite@2.39.1/rive.js';
  var MQ = '(max-width: 768px)';
  var loadPromise = null;
  var bufferCache = {};
  var fetchCache = {};

  function loadRiveRuntime() {
    if (window.rive && typeof window.rive.Rive === 'function') {
      return Promise.resolve(window.rive);
    }
    if (loadPromise) return loadPromise;

    loadPromise = new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[data-hero-rive-runtime]');
      if (existing) {
        if (window.rive && typeof window.rive.Rive === 'function') {
          resolve(window.rive);
          return;
        }
        existing.addEventListener('load', function () {
          resolve(window.rive);
        });
        existing.addEventListener('error', reject);
        return;
      }

      var script = document.createElement('script');
      script.src = RIVE_CDN;
      script.async = true;
      script.setAttribute('data-hero-rive-runtime', 'true');
      script.onload = function () {
        resolve(window.rive);
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });

    return loadPromise;
  }

  function fetchBuffer(src) {
    if (bufferCache[src]) return Promise.resolve(bufferCache[src]);
    if (fetchCache[src]) return fetchCache[src];

    fetchCache[src] = fetch(src, { credentials: 'omit', mode: 'cors' })
      .then(function (res) {
        if (!res.ok) throw new Error('Failed to load Rive file');
        return res.arrayBuffer();
      })
      .then(function (buffer) {
        bufferCache[src] = buffer;
        delete fetchCache[src];
        return buffer;
      })
      .catch(function (err) {
        delete fetchCache[src];
        throw err;
      });

    return fetchCache[src];
  }

  function cloneBuffer(buffer) {
    return buffer.slice(0);
  }

  function HeroRive(root) {
    this.root = root;
    this.canvas = root.querySelector('[data-hero-rive-canvas]');
    this.desktopSrc = (root.getAttribute('data-rive-desktop') || '').trim();
    this.mobileSrc = (root.getAttribute('data-rive-mobile') || '').trim();
    this.mq = window.matchMedia(MQ);
    this.instance = null;
    this.currentSrc = '';
    this.loadToken = 0;
    this._onChange = this.sync.bind(this);
    this._onResize = this.resize.bind(this);
    this._ro = null;

    if (!this.canvas || (!this.desktopSrc && !this.mobileSrc)) return;

    // Prefetch both files so breakpoint swaps are instant
    if (this.desktopSrc) fetchBuffer(this.desktopSrc).catch(function () {});
    if (this.mobileSrc && this.mobileSrc !== this.desktopSrc) {
      fetchBuffer(this.mobileSrc).catch(function () {});
    }

    if (typeof this.mq.addEventListener === 'function') {
      this.mq.addEventListener('change', this._onChange);
    } else if (typeof this.mq.addListener === 'function') {
      this.mq.addListener(this._onChange);
    }

    window.addEventListener('resize', this._onResize);

    if (typeof ResizeObserver === 'function') {
      this._ro = new ResizeObserver(this._onResize);
      this._ro.observe(this.canvas);
    }

    this.sync();
  }

  HeroRive.prototype.activeSrc = function () {
    if (this.mq.matches) {
      return this.mobileSrc || this.desktopSrc;
    }
    return this.desktopSrc || this.mobileSrc;
  };

  HeroRive.prototype.setRiveActive = function (active) {
    this.root.classList.toggle('hero-banner__media--rive-active', !!active);
  };

  HeroRive.prototype.resize = function () {
    if (!this.instance || typeof this.instance.resizeDrawingSurfaceToCanvas !== 'function') {
      return;
    }
    if (!this.root.classList.contains('hero-banner__media--rive-active')) return;
    if (this.canvas.clientWidth < 2 || this.canvas.clientHeight < 2) return;
    this.instance.resizeDrawingSurfaceToCanvas();
  };

  HeroRive.prototype.destroyInstance = function () {
    if (!this.instance) return;
    try {
      if (typeof this.instance.cleanup === 'function') {
        this.instance.cleanup();
      } else if (typeof this.instance.stop === 'function') {
        this.instance.stop();
      }
    } catch (e) {
      /* ignore */
    }
    this.instance = null;
    this.currentSrc = '';
  };

  HeroRive.prototype.ensurePlaying = function () {
    var instance = this.instance;
    if (!instance) return;

    try {
      if (typeof instance.isPlaying === 'boolean' && instance.isPlaying) return;

      var names = [];
      if (instance.stateMachineNames && instance.stateMachineNames.length) {
        names = instance.stateMachineNames;
      } else if (instance.animationNames && instance.animationNames.length) {
        names = instance.animationNames;
      }

      if (names.length) {
        instance.play(names);
      } else {
        instance.play();
      }
    } catch (e) {
      try {
        instance.play();
      } catch (err) {
        /* ignore */
      }
    }
  };

  HeroRive.prototype.mount = function (src, buffer, riveApi) {
    var self = this;
    this.destroyInstance();
    this.currentSrc = src;
    this.setRiveActive(true);

    var layout;
    if (riveApi.Layout && riveApi.Fit) {
      layout = new riveApi.Layout({
        fit: riveApi.Fit.Cover,
        alignment: riveApi.Alignment ? riveApi.Alignment.Center : undefined
      });
    }

    var config = {
      canvas: this.canvas,
      autoplay: true,
      layout: layout,
      onLoad: function () {
        self.resize();
        self.ensurePlaying();
        requestAnimationFrame(function () {
          self.resize();
          self.ensurePlaying();
        });
      },
      onStop: function () {
        // Force continuous loop when the .riv is a one-shot (~8–10s)
        if (self.instance === instance && self.currentSrc === src) {
          self.ensurePlaying();
        }
      }
    };

    if (buffer) {
      config.buffer = cloneBuffer(buffer);
    } else {
      config.src = src;
    }

    var instance = new riveApi.Rive(config);
    this.instance = instance;
  };

  HeroRive.prototype.sync = function () {
    var self = this;
    var src = this.activeSrc();

    if (!src) {
      this.loadToken += 1;
      this.destroyInstance();
      this.setRiveActive(false);
      return;
    }

    // Same file already mounted — just resize / resume after breakpoint paint
    if (src === this.currentSrc && this.instance) {
      this.setRiveActive(true);
      requestAnimationFrame(function () {
        self.resize();
        self.ensurePlaying();
      });
      return;
    }

    var token = ++this.loadToken;

    loadRiveRuntime()
      .then(function (riveApi) {
        if (token !== self.loadToken) return null;
        return fetchBuffer(src)
          .then(function (buffer) {
            return { riveApi: riveApi, buffer: buffer };
          })
          .catch(function () {
            // CORS or fetch failure — let Rive load via src directly
            return { riveApi: riveApi, buffer: null };
          });
      })
      .then(function (payload) {
        if (!payload || token !== self.loadToken) return;
        if (!payload.riveApi || typeof payload.riveApi.Rive !== 'function') return;
        self.mount(src, payload.buffer, payload.riveApi);
      })
      .catch(function () {
        if (token !== self.loadToken) return;
        self.destroyInstance();
        self.setRiveActive(false);
      });
  };

  HeroRive.prototype.destroy = function () {
    this.loadToken += 1;
    this.destroyInstance();

    if (typeof this.mq.removeEventListener === 'function') {
      this.mq.removeEventListener('change', this._onChange);
    } else if (typeof this.mq.removeListener === 'function') {
      this.mq.removeListener(this._onChange);
    }

    window.removeEventListener('resize', this._onResize);
    if (this._ro) {
      this._ro.disconnect();
      this._ro = null;
    }
  };

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-hero-rive-root]');
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      if (root._heroRive) {
        root._heroRive.destroy();
      }
      root._heroRive = new HeroRive(root);
    }
  }

  // Start loading the runtime immediately
  loadRiveRuntime().catch(function () {});

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var roots = event.target.querySelectorAll('[data-hero-rive-root]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i]._heroRive) {
        roots[i]._heroRive.destroy();
        roots[i]._heroRive = null;
      }
    }
  });
})();
